<template>
  <div class="chips-row">
    <template v-for="(item, index) in infoItems" :key="`info-item-${index}`">
      <div
        v-if="item?.label?.value"
        :data-id="item.type"
        class="chip"
        :class="'chip--' + item.type"
      >
        <component :is="item?.icon" class="chip-icon" />
        <span v-html="item?.label?.value"></span>
        <Tooltip
          v-if="item.type === 'people'"
          :tooltip-text="$t('product.show.infoBanner.people.tooltip')"
          length="medium"
          position="up"
          type="span"
        >
          <Info class="chip-info-icon" />
        </Tooltip>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  Accessibility as IconAccessibility,
  Clock as IconDuration,
  Info,
  Languages as IconLanguages,
  MapPin as IconLocation,
  Users as IconPeople,
} from 'lucide-vue-next'

import type { EventDescription } from '~/composables/types/event'

type ChipType =
  | 'duration'
  | 'languages'
  | 'people'
  | 'location'
  | 'accessibility'

const {
  eventData = null,
  isGiftcard = false,
  items = ['duration', 'languages', 'people', 'location', 'accessibility'],
} = defineProps<{
  eventData?: EventDescription | null
  isGiftcard?: boolean
  /** Which chips to render, in the fixed order above. Defaults to all. */
  items?: ChipType[]
}>()

const { t: $t } = useI18n()

const locationLabel = computed(() => {
  if (eventData?.type === 'ONLINE') return 'Online'

  if (eventData?.has_mobile_classes)
    return $t('product.show.infoBanner.atYourLocation')

  if (eventGetters.getHasMultipleLocations(eventData as EventDescription))
    return $t('product.show.giftcardIcons.multipleLocationsSummary')

  return eventData?.address?.data?.locality?.data?.name
})

const durationLabel = computed(() => {
  if (isGiftcard) return $t('product.show.giftcardIcons.durationContent')

  const multiDateDuration =
    eventGetters.getMultiDateDurationPerSession(eventData)
  const durationInHumanReadableString = multiDateDuration
    ? `${convertMinutesToHumanString(multiDateDuration)}  ${$t('product.show.importantDetails.perSession')}`
    : convertMinutesToHumanString(
        +eventGetters.getNextDateAvailableDuration(
          eventData as EventDescription | undefined
        ) ||
          +eventGetters.getDefaultDuration(
            eventData as EventDescription | undefined
          ) ||
          +eventGetters.getEventMinimumDuration(
            eventData as EventDescription | undefined
          ) ||
          +eventGetters.getEventMaximumDuration(
            eventData as EventDescription | undefined
          )
      )
  return (
    '<span class="hidden md:inline-block">' +
    $t('product.show.infoBanner.duration') +
    '</span>' +
    ' ' +
    durationInHumanReadableString
  )
})

const languagesLabel = computed(() => {
  return isGiftcard
    ? $t('product.show.giftcardIcons.languages.content')
    : eventGetters
        .getLanguages(eventData as EventDescription)
        ?.map((i) => $t(`product.show.icons.languages.list.${i}`))
        .join(', ')
})

// Rendered only for accessible venues — an absent chip is not a claim that the
// venue is inaccessible, so no negative state is shown.
const accessibilityLabel = computed(() => {
  return eventGetters.getIsAccessible(eventData)
    ? $t('product.show.importantDetails.accessibility')
    : ''
})

// Mirrors product.show.infoBanner.people's min/max/same/range shape — no
// chip when neither bound is known, matching the accessibility chip's
// no-negative-state convention.
const peopleLabel = computed(() => {
  const min = eventData?.min_number_of_people
  const max = eventData?.max_number_of_people

  if (min == null && max == null) return ''
  if (min != null && max != null && min === max)
    return $t('product.show.infoBanner.people.same', { n: min })
  if (min != null && max == null)
    return $t('product.show.infoBanner.people.minOnly', { n: min })
  if (min == null && max != null)
    return $t('product.show.infoBanner.people.maxOnly', { n: max })

  return $t('product.show.infoBanner.people.range', { min, max })
})

const infoItems = computed(() => {
  const all = [
    {
      type: 'duration',
      icon: IconDuration,
      label: durationLabel,
    },
    {
      type: 'languages',
      icon: IconLanguages,
      label: languagesLabel,
    },
    {
      type: 'people',
      icon: IconPeople,
      label: peopleLabel,
    },
    {
      type: 'location',
      icon: IconLocation,
      label: locationLabel,
    },
    {
      type: 'accessibility',
      icon: IconAccessibility,
      label: accessibilityLabel,
    },
  ]

  return all.filter((item) => items.includes(item.type as ChipType))
})
</script>

<style lang="css" scoped>
@reference '~/assets/css/tailwind.css';
.chips-row {
  @apply flex w-full flex-wrap items-center gap-2 my-4 md:my-5;
}

.chip {
  @apply inline-flex shrink-0 cursor-default items-center justify-center gap-2 rounded-full  border-teal-300 bg-teal-50 px-4 py-1.5 text-left text-sm font-normal leading-none text-stone-900 antialiased;
  @apply max-w-full;
}

.chip:focus-visible {
  @apply outline outline-2 outline-offset-2 outline-stone-900;
}

.chip--people,
.chip--duration {
  @apply whitespace-nowrap;
}

.chip-icon {
  @apply size-5 min-w-5 shrink-0 text-stone-900;
}

.chip-info-icon {
  @apply size-3.5 min-w-3.5 shrink-0 text-stone-500;
}
</style>
